import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

const CheckInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [passengerData, setPassengerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  // Check if this is being used within JetSetter dashboard
  const isJetSetterContext = location.pathname.includes('/jetsetter');

  useEffect(() => {
    // Simulate API call to fetch passenger data
    setTimeout(() => {
      setPassengerData([
        {
          serial_no: 1,
          passenger_id: 'P001',
          name: 'JOHN DOE',
          passport_no: '',
          expiry_date: ''
        },
        {
          serial_no: 2,
          passenger_id: 'P002',
          name: 'JANE DOE',
          passport_no: '',
          expiry_date: ''
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const handleInputChange = (serialNo, field, value) => {
    setPassengerData(prev => 
      prev.map(passenger => 
        passenger.serial_no === serialNo 
          ? { ...passenger, [field]: value }
          : passenger
      )
    );
    
    // Clear error for this field
    setErrors(prev => ({
      ...prev,
      [`${serialNo}_${field}`]: ''
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    passengerData.forEach(passenger => {
      // Validate passport number
      if (!passenger.passport_no) {
        newErrors[`${passenger.serial_no}_passport_no`] = 'This field is required';
        isValid = false;
      } else if (passenger.passport_no.length < 6 || passenger.passport_no.length > 9 || !/^[A-Za-z0-9]+$/.test(passenger.passport_no)) {
        newErrors[`${passenger.serial_no}_passport_no`] = 'Please enter a valid passport number.';
        isValid = false;
      }

      // Validate expiry date
      if (!passenger.expiry_date) {
        newErrors[`${passenger.serial_no}_expiry_date`] = 'This field is required';
        isValid = false;
      } else if (!/^\d{2}-\d{2}-\d{4}$/.test(passenger.expiry_date)) {
        newErrors[`${passenger.serial_no}_expiry_date`] = 'Please enter a valid date format (dd-mm-yyyy).';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm('Please ensure that all Passenger\'s Passport details are entered correctly before proceeding to check-in.');
    
    if (confirmed) {
      // Simulate API call
      setTimeout(() => {
        toast.success('Check-in successful!');
        if (isJetSetterContext) {
          navigate('/jetsetter/profile/booking-history');
        } else {
          navigate('/boarding-pass');
        }
      }, 1000);
    } else {
      // Allow editing — stay on form
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading check-in form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4 mt-36 max-md:mt-24 max-sm:mt-16 mb-8">
      <div className="w-full">
          <div className="card">
            <div className="card-head">
              <div className="header-title">
                <span className="card-title">Check-In</span>
              </div>
              <div className="description">
                <p>Enter passport details for all passengers to complete the check-in process. Ensure the information matches your travel documents for a smooth experience.</p>
              </div>
            </div>
            
            <div className="card-body">
              <form id="checkinForm" onSubmit={handleSubmit}>
                <div className="passenger-container">
                  {passengerData.map((passenger) => (
                    <div key={passenger.serial_no} className="mb-4">
                      <input type="hidden" name={`passenger[${passenger.serial_no}][id]`} value={passenger.passenger_id} />
                      <label className="block font-medium text-gray-700 mb-1">Passenger {passenger.serial_no}</label>
                      <div className="passenger-details">
                        <div className="in-field">
                          <div className="input-group mb-3" style={{ minHeight: '48px', border: '1px solid #b5bac1', borderRadius: '5px', background: '#f3f3f3' }}>
                            <span className="input-group-text" style={{ border: 'unset', background: 'unset' }}>
                              <i className="fas fa-user"></i>
                            </span>
                            <input 
                              type="text" 
                              className="form-control" 
                              style={{ minHeight: '48px', border: 'unset', textTransform: 'uppercase', fontSize: '14px' }}
                              placeholder="Passenger Name" 
                              name={`passenger[${passenger.serial_no}][name]`}
                              value={passenger.name}
                              disabled
                            />
                          </div>
                        </div>
                        <div className="in-field">
                          <div className="input-group mb-3" style={{ minHeight: '48px', border: '1px solid #b5bac1', borderRadius: '5px', background: '#f3f3f3', fontSize: '20px' }}>
                            <span className="input-group-text" style={{ border: 'unset', background: 'unset' }}>
                              <i className="fas fa-passport"></i>
                            </span>
                            <input 
                              type="text" 
                              className="form-control" 
                              style={{ 
                                minHeight: '48px', 
                                border: 'unset', 
                                fontSize: '14px', 
                                textTransform: 'uppercase',
                                borderColor: errors[`${passenger.serial_no}_passport_no`] ? 'red' : ''
                              }}
                              placeholder="Passport Number" 
                              name={`passenger[${passenger.serial_no}][passport_no]`}
                              value={passenger.passport_no}
                              onChange={(e) => handleInputChange(passenger.serial_no, 'passport_no', e.target.value)}
                              required
                            />
                          </div>
                          {errors[`${passenger.serial_no}_passport_no`] && (
                            <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                              {errors[`${passenger.serial_no}_passport_no`]}
                            </div>
                          )}
                        </div>
                        <div className="in-field in-field-date">
                          <div className="input-group mb-3" style={{ minHeight: '48px', border: '1px solid #b5bac1', borderRadius: '5px', background: '#f3f3f3' }}>
                            <span className="input-group-text" style={{ border: 'unset', background: 'unset' }}>
                              <i className="fas fa-calendar"></i>
                            </span>
                            <input 
                              type="text" 
                              className="form-control fc-datepickercheckin" 
                              style={{ 
                                minHeight: '48px', 
                                border: 'unset', 
                                paddingLeft: '10px', 
                                fontSize: '14px', 
                                textTransform: 'uppercase',
                                borderColor: errors[`${passenger.serial_no}_expiry_date`] ? 'red' : ''
                              }}
                              name={`passenger[${passenger.serial_no}][expiry_date]`}
                              placeholder="Expiry Date"
                              value={passenger.expiry_date}
                              onChange={(e) => handleInputChange(passenger.serial_no, 'expiry_date', e.target.value)}
                              required
                            />
                          </div>
                          {errors[`${passenger.serial_no}_expiry_date`] && (
                            <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                              {errors[`${passenger.serial_no}_expiry_date`]}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button type="submit" className="btn-checkin checkin" style={{ marginTop: '15px' }}>
                  CHECK-IN
                </button>
              </form>
            </div>
          </div>
      </div>

      <style jsx>{`
        .card {
          padding: 2.5rem;
          border-radius: 10px;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
          background: #fff;
        }
        @media (max-width: 767px) {
          .card {
            padding: 1.25rem;
          }
        }
        .header-title {
          text-align: center;
        }
        .card-head {
          margin: 0 6.5rem 0 6.5rem;
        }
        @media (max-width: 767px) {
          .card-head {
            margin: 0;
          }
        }
        .card-title {
          font-family: 'Inter', sans-serif !important;
          font-size: 40px !important;
          font-weight: 500 !important;
          color: #676767 !important;
        }
        @media (max-width: 767px) {
          .card-title {
            font-size: 28px !important;
          }
        }
        .description {
          text-align: center;
        }
        .description p {
          font-family: 'Bahnschrift', sans-serif;
          font-size: 14px;
          color: #676767;
          line-height: 1.4;
        }
        .input-group {
          display: flex;
          align-items: center;
          width: 100%;
        }
        .input-group-text {
          background-color: #f8f9fa;
          border-right: 0;
          padding: 0 10px;
          display: flex;
          align-items: center;
        }
        .form-control {
          border-left: 0;
          background-color: #f3f3f3;
          flex: 1;
          width: 100%;
          outline: none;
        }
        .btn-checkin {
          background: #ffc107;
          border: none;
          padding: 12px;
          font-size: 16px;
          font-weight: bold;
          width: 100%;
          border-radius: 5px;
          cursor: pointer;
          color: black;
          text-transform: uppercase;
          font-family: 'Bahnschrift' !important;
        }
        .btn-checkin:hover {
          background: #e0a800;
        }
        .passenger-details {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          align-items: flex-start;
          padding-top: 10px;
          padding-bottom: 10px;
        }
        .in-field {
          margin-right: 10px;
          flex: 1 1 0;
          min-width: 0;
        }
        .in-field-date {
          flex: 0 1 160px;
          margin-right: 0;
        }
        @media (max-width: 767px) {
          .passenger-details {
            flex-direction: column;
            gap: 0;
          }
          .in-field,
          .in-field-date {
            margin-right: 0;
            flex: none;
            width: 100%;
          }
        }
        .form-control:disabled, .form-control[readonly] {
          background-color: unset !important;
        }
        .input-group-text i {
          color: #858585;
        }
        .passenger-container {
          max-height: 500px;
          overflow-y: auto;
        }
        .passenger-container::-webkit-scrollbar {
          width: 8px;
        }
        .passenger-container::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        .passenger-container::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </div>
  );
};

export default CheckInPage;
